<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('service.services') }}</span>
        <h3 class="page-title">{{ $t('service.group.editServiceGroup') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/zdobienia/grupy')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          {{ $t('ui.saveChanges') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form :class="{ 'is-loading': !allDataLoaded }">
      <d-card class="mb-5">
        <d-tabs v-sticky-tabs
          v-if="!loadError"
          card>
          <!-- Dane ogólne -->
          <d-tab
            :title="$t('ui.generalInfo')"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              {{ $t('ui.generalInfo') }}
            </h4>

            <validation-observer ref="services-group-edit-form" tag="form">
              <div class="form-group">
                <d-checkbox
                  inline
                  v-model="is_package"
                  toggle
                  toggle-small
                  class="mt-1">
                  {{ $t('service.isPackage') }}
                </d-checkbox>
              </div>

              <div
                v-if="is_package"
                :class="{
                  'form-control': true,
                  'is-select': true,
                  'mb-3': true
                }">
                <v-select
                  class="w-100"
                  :options="services"
                  v-model="package_services"
                  :multiple="true"
                  :reduce="service => service.id"
                  :placeholder="$t('service.selectService')"
                  label="code"
                  :filterBy="filterByForServices">
                  <template slot="no-options">
                    {{ $t('service.noServicesMatchingPhrase') }}&hellip;
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center">
                      {{ option.group_name }} - {{ option.name }}
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      {{ option.group_name }} - {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('service.group.serviceGroupName') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('service.group.serviceGroupCode') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="code"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="code" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('service.embroidery.type') }}:
                </label>
                <div
                  class="form-control is-select">
                  <v-select
                    :options="embroideryTypes"
                    v-model="embroidery_type_id"
                    :placeholder="$t('service.chooseEmbroideryType')"
                    :reduce="embroideryType => embroideryType.id"
                    :searchable="false"
                    label="name">
                  </v-select>
                </div>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('service.group.assortmentGroupName') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="assortment_group"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="assortment_group" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('service.group.serviceGroupPhoto') }}:
                </label>

                <div v-if="image_url">
                  <img :src="image_url" />
                </div>

                <div v-if="image_url">
                  <a
                    href="#"
                    @click.prevent="removeServiceImage()"
                    class="text-danger">
                    <small>{{ $t('image.removePhoto') }}</small>
                  </a>
                </div>

                <file-input
                  v-if="!image_url"
                  :onChange="setServiceImage"
                  id="inputFileServiceImage" />
              </div>
            </validation-observer>
          </d-tab>
          <d-tab
            :title="$t('service.group.servicesVariants')"
            :disabled="is_package">
            <h4 class="mb-3 pb-3 border-bottom d-flex">
              {{ $t('service.group.servicesVariants') }}
              <d-button
                class="ml-auto"
                theme="success"
                @click.prevent="addVariant">
                <i class="material-icons">add</i>
                {{ $t('service.addVariant') }}
              </d-button>
            </h4>

            <table
              v-if="loadedVariants && !loadVariantsError"
              class="table mt-2 mb-0 full-width">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0">{{ $t('service.serviceName') }}</th>
                  <th scope="col" class="border-0">{{ $t('service.serviceCode') }}</th>
                  <th scope="col" class="border-0">{{ $t('service.setupPrice') }}</th>
                  <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(variant, index) of variants"
                  :key="'service-' + index">
                  <td class="align-middle">
                    {{ variant.name }}
                    <template v-if="variant.available_in_embriodery">
                      <br>
                      <d-badge theme="success">
                        {{ $t('service.availableInHafciarnia') }}
                      </d-badge>
                    </template>
                  </td>
                  <td class="align-middle">
                    {{ variant.code }}
                  </td>
                  <td class="align-middle" v-pure-html="getSetupPrices(variant.service_prices)"></td>
                  <td
                    class="align-middle"
                    style="width: 200px;">
                    <d-button
                      theme="primary"
                      @click.native.prevent="editVariant(variant)"
                      class="mr-2"
                      :title="$t('ui.edit')">
                      <i class="material-icons">edit</i>
                    </d-button>
                    <d-button
                      theme="danger"
                      :disabled="removeInProgress"
                      @click.native.prevent="removeVariant(variant.id)"
                      :title="$t('ui.delete')">
                      <i class="material-icons">delete</i>
                    </d-button>
                  </td>
                </tr>
                <tr v-if="variants.length === 0">
                  <td
                    colspan="4"
                    class="text-center">
                    {{ $t('service.noServicesToDisplay') }}&hellip;
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="!loadedVariants" class="pb-3 pt-3 text-center">
              {{ $t('ui.dataLoading') }}&hellip;
            </div>

            <d-alert
              v-if="loadVariantsError"
              show
              theme="warning">
              {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
            </d-alert>
          </d-tab>

          <d-tab :title="$t('settings.transaltion.translations')">
            <h4 class="pb-3 mb-0 border-bottom d-flex">
              {{ $t('settings.transaltion.translations') }}

              <d-button
                class="ml-auto"
                theme="success"
                @click.prevent="addTranslation">
                <i class="material-icons">add</i>
                {{ $t('settings.transaltion.addTranslation') }}
              </d-button>
            </h4>

            <table class="table mt-2 mb-0 full-width">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.language') }}</th>
                  <th scope="col" class="border-0" style="width: calc(100% - 350px);">{{ $t('service.group.serviceGroupName') }}</th>
                  <th scope="col" class="border-0" style="width: 200px;">{{ $t('ui.actions') }}</th>
                </tr>
              </thead>
              <tbody v-if="group_translations.length">
                <tr
                  v-for="(translation, index) of group_translations"
                  :key="'price-' + index">
                  <td class="align-middle">
                    {{ getLanguageByID(translation.language_id) }}
                  </td>
                  <td class="align-middle">
                    {{ translation.name }}
                  </td>
                  <td class="align-middle">
                    <d-button
                      theme="primary"
                      @click.prevent="editTranslation(translation)"
                      class="mr-2"
                      :title="$t('ui.edit')">
                      <i class="material-icons">edit</i>
                    </d-button>
                    <d-button
                      theme="danger"
                      :disabled="removeTranslationInProgress"
                      @click.prevent="removeTranslation(translation.id)"
                      :title="$t('ui.delete')">
                      <i class="material-icons">delete</i>
                    </d-button>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="3" class="text-center">
                    {{ $t('service.group.noTranslationsForThisServiceGroup') }}&hellip;
                  </td>
                </tr>
              </tbody>
            </table>
          </d-tab>
        </d-tabs>

        <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
          {{ $t('ui.dataLoading') }}&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
        </d-alert>
      </d-card>
    </d-form>

    <service-new-popup
      v-if="showAddPopup"
      :groupID="this.id" />

    <service-edit-popup
      v-if="showEditPopup"
      :groupID="this.id"
      :serviceData="this.selectedVariant" />

    <service-translation-new-popup
      v-if="showAddTranslationPopup"
      :serviceGroupID="this.id"
      :languages="languages" />

    <service-translation-edit-popup
      v-if="showEditTranslationPopup"
      :serviceGroupID="this.id"
      :translationData="selectedTranslation"
      :languages="languages" />
  </d-container>
</template>

<script>
import CommonGetters from './../../utils/CommonGetters.js';
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';
import ServiceNewPopup from './ServiceNewPopup.vue';
import ServiceEditPopup from './ServiceEditPopup.vue';
import ServiceTranslationNewPopup from './ServiceTranslationNewPopup';
import ServiceTranslationEditPopup from './ServiceTranslationEditPopup';

export default {
  name: 'services-group-edit',
  components: {
    'service-new-popup': ServiceNewPopup,
    'service-edit-popup': ServiceEditPopup,
    'service-translation-new-popup': ServiceTranslationNewPopup,
    'service-translation-edit-popup': ServiceTranslationEditPopup
  },
  computed: {
    allDataLoaded () {
      return this.loadedData && this.loadedServices && this.loadedCurrencies && this.loadedLanguages && this.loadedEmbroideryItems;
    },
    embroideryTypes () {
      let langToUse = this.$store.getters.getUserLang;

      if (!this.embroideryItems || !this.embroideryItems.types) {
        return [];
      }

      return this.embroideryItems.types.map(item => {
        let availableTranslation = item.translations.filter(translation => translation.language_id === langToUse);
        let itemName = item.name;

        if (availableTranslation.length) {
          itemName = availableTranslation[0].name;
        }

        return {
          id: item.id,
          name: itemName
        };
      });
    }
  },
  data() {
    return {
      id: 0,
      code: '',
      assortment_group: '',
      image: '',
      image_url: '',
      is_package: false,
      currencies: [],
      name: '',
      embroidery_type_id: null,
      variants: [],
      package_services: [],
      loadedData: false,
      loadedVariants: true,
      loadedCurrencies: false,
      loadedLanguages: false,
      loadedServices: false,
      loadError: false,
      loadVariantsError: false,
      showAddPopup: false,
      showEditPopup: false,
      selectedVariant: false,
      removeInProgress: false,
      group_translations: [],
      services: [],
      types: [],
      languages: [],
      showAddTranslationPopup: false,
      showEditTranslationPopup: false,
      selectedTranslation: 0,
      removeTranslationInProgress: false,
      embroideryItems: [],
      loadedEmbroideryItems: false
    };
  },
  mounted () {
    FormUtils.loadItemData(this, {
      method: 'get',
      endpointBase: '/services/groups/item/',
      instanceFields: ['name', 'code', 'is_package', 'assortment_group', 'image', 'image_url', 'group_translations', 'embroidery_type_id', 'package_services'],
      booleanFields: ['is_package'],
      loadedKey: 'loadedData',
      params: {
        with: ['groupTranslations', 'packageServices']
      }
    });

    this.$bus.$on('group-add-service-popup-close', this.closeAddPopup);
    this.$bus.$on('group-edit-service-popup-close', this.closeEditPopup);
    this.$bus.$on('group-reload-services', this.loadVariants);
    this.loadVariants();
    this.loadAdditionalData();
    this.loadLanguages();
    this.$bus.$on('service-group-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$on('service-group-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$on('service-group-reload-translations', this.loadTranslations);
  },
  methods: {
    filterByForServices (option, label, search) {
      return (option.code + ' ' + option.group_name + ' ' + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    validateForm () {
      FormUtils.validate(this.$refs['services-group-edit-form'], this.submitForm);
    },
    submitForm () {
      let imageFields = false;
      let formDataToSend = {
        name: this.name,
        code: this.code,
        is_package: this.is_package,
        assortment_group: this.assortment_group,
        embroidery_type_id: this.embroidery_type_id
      };

      if (this.is_package && this.package_services) {
        formDataToSend.package_services = this.package_services;
      } else {
        formDataToSend.package_services = [];
      }

      if (this.image) {
        formDataToSend.image = this.image;
        imageFields = ['image'];
      } else {
        formDataToSend.image = null;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/services/groups/update/' + this.id,
        formData: formDataToSend,
        successTitle: this.$t('service.group.updateServiceGroupSuccessTitle'),
        successText: this.$t('service.group.updateServiceGroupSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('service.group.updateServiceGroupErrorText'),
        successAction: () => {
          FormUtils.loadItemData(this, {
            method: 'get',
            endpointBase: '/services/groups/item/',
            instanceFields: ['image', 'image_url'],
            loadedKey: 'loadedData',
            errorKey: 'loadError'
          });

          this.loadVariants();
        }
      }, imageFields);
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/services/all-items',
        loadedKey: 'loadedServices',
        outputKey: 'services',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/embroidery/items',
        loadedKey: 'loadedEmbroideryItems',
        outputKey: 'embroideryItems',
        errorKey: 'loadError'
      });
    },
    loadVariants () {
      this.loadedVariants = false;

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/services/items',
        params: {
          with: ['servicePrices'],
          where: {
            group_id: this.id
          }
        },
        outputKey: 'variants',
        loadedKey: 'loadedVariants',
        errorKey: 'loadVariantsError'
      });
    },
    addVariant () {
      this.showAddPopup = true;
    },
    editVariant (variant) {
      this.selectedVariant = JSON.parse(JSON.stringify(variant));
      this.showEditPopup = true;
    },
    removeVariant (variantID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/services/delete/',
        id: variantID,
        successTitle: this.$t('service.deleteSuccessText'),
        successText: this.$t('service.deleteSuccessText'),
        confirmTitle: this.$t('service.deleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('service.deleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadVariants();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    closeAddPopup () {
      this.showAddPopup = false;
    },
    closeEditPopup () {
      this.showEditPopup = false;
    },
    getSetupPrices (servicePrices) {
      let output = [];
      let setupPrices = servicePrices.filter(price => price.is_setup);
      setupPrices.sort((itemA, itemB) => itemA.value_from - itemB.value_from);

      for (let i = 0; i < setupPrices.length; i++) {
        output.push(Number(setupPrices[i].price).toFixed(2) + ' ' + this.getCurrency(setupPrices[i].currency_id) + ' (' + this.$t('ui.to') + ' ' + parseInt(setupPrices[i].value_from, 10) + ' ' + this.$t('ui.piecesAbbr') + ')');
      }

      return '<small>' + output.join('<br>') + '</small>';
    },
    getCurrency (currencyID) {
      return CommonGetters.getCurrencyById(currencyID, this.currencies);
    },
    setServiceImage (event) {
      this.image = event.target.files[0];
    },
    removeServiceImage () {
      this.image_url = '';
      this.image = '';
    },
    loadLanguages () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/languages/items',
        loadedKey: 'loadedLanguages',
        outputKey: 'languages',
        errorKey: 'loadError'
      });
    },
    getLanguageByID (languageID) {
      let filteredLanguages = this.languages.filter(language => language.id === languageID);

      if (filteredLanguages.length) {
        return filteredLanguages[0].name;
      }

      return '';
    },
    loadTranslations () {
      FormUtils.loadItemData(this, {
        method: 'get',
        endpointBase: '/services/groups/item/',
        instanceFields: ['group_translations'],
        loadedKey: null,
        params: {
          with: ['groupTranslations']
        }
      });
    },
    addTranslation () {
      this.showAddTranslationPopup = true;
    },
    editTranslation (translationData) {
      this.selectedTranslation = JSON.parse(JSON.stringify(translationData));
      this.showEditTranslationPopup = true;
    },
    closeAddTranslationPopup () {
      this.showAddTranslationPopup = false;
    },
    closeEditTranslationPopup () {
      this.showEditTranslationPopup = false;
    },
    removeTranslation (translationID) {
      this.removeTranslationInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/services/groups/translations/delete/',
        id: translationID,
        successTitle: this.$t('settings.transaltion.deleteTranslationSuccessTitle'),
        successText: this.$t('settings.transaltion.deleteTranslationSuccessText'),
        confirmTitle: this.$t('settings.transaltion.deleteTranslationConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('settings.transaltion.deleteTranslationErrorText'),
        successAction: () => {
          this.removeTranslationInProgress = false;
          this.loadTranslations();
        },
        cancelAction: () => {
          this.removeTranslationInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('group-add-service-popup-close', this.closeAddPopup);
    this.$bus.$off('group-edit-service-popup-close', this.closeEditPopup);
    this.$bus.$off('group-reload-services', this.loadVariants);
    this.$bus.$off('service-group-translation-new-popup-close', this.closeAddTranslationPopup);
    this.$bus.$off('service-group-translation-edit-popup-close', this.closeEditTranslationPopup);
    this.$bus.$off('service-group-reload-translations', this.loadTranslations);
  }
};
</script>

<style lang="scss" scoped>
form.is-loading {
  opacity: .25;
  pointer-events: none;
}
</style>
